import {
  Article,
  ArticleSortType,
  ArticleType,
  Category,
} from '@wix/answers-api';
import { isProduction } from '@wix/wix-run-mode';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { CategoryPageWrapper } from '../../components/CategoryPageWrapper';
import {
  KNOWN_ISSUES_ITEMS_PER_PAGE,
  KnownIssuesPage,
} from '../../components/KnownIssuesPage';
import { KNOWN_ISSUE_CATEGORY_ID } from '../../constants';
import {
  getCategoryArticles,
  getCategoryByUri,
  getCategoryTranslations,
  getPageSeo,
  searchArticles,
} from '../../services/answers';
import { getWixDocsUrl } from '../../services/wix-docs-redirect';
import {
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  HttpStatusCode,
  KNOWN_ISSUES_RELEVANT_RESOLUTIONS,
  PageType,
} from '../../types';
import { answersApi } from '../../utils/answers';
import { findCategoryInTree } from '../../utils/categories';
import { isKbServiceEnabled } from '../../utils/experiments';
import { LoggerNamespaces, createLogger } from '../../utils/logger';
import { isSledUserAgent } from '../../utils/user-agent';
import { devFallback, withServerSideProps } from '../../utils/with-server-side-props';
import { useStore } from '../../zustand/stores';

type CategoryPageProps = {
  category: Category;
  categoriesArticles: Record<string, Article[]>;
  knownIssuesArticles: ArticleSearchResultItem[] | null;
  knownIssuesCount: number | null;
};

const CategoryPage: NextPage<CategoryPageProps> = ({
  category,
  categoriesArticles,
  knownIssuesArticles,
  knownIssuesCount,
}) => {
  const { locale } = useRouter();
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { user } = useStore((state) => state.userSlice);
  const [usersFollowedArticles, setUsersFollowedArticles] = useState<Article[]>(
    []
  );

  const isKnownIssuePage = category.id === KNOWN_ISSUE_CATEGORY_ID;

  const getFollowedArticles = useCallback(async () => {
    try {
      const userFollowedArticleResponse =
        await answersApi.articles.getFollowedArticles({
          locale: locale as string,
          page: 1,
          pageSize: 50,
        });
      setUsersFollowedArticles(userFollowedArticleResponse.items);
    } catch (e) {
      setUsersFollowedArticles([]);
    }
  }, [answersApi, locale]);

  useEffect(() => {
    void (async () => {
      if (isKnownIssuePage && user !== null) {
        await getFollowedArticles();
      }
    })();
  }, [getFollowedArticles, isKnownIssuePage, user]);

  const followedKnownIssuesIds = usersFollowedArticles
    .filter((article) => article.type === ArticleType.KNOWN_ISSUE)
    .map((article) => article.id);
  return isKnownIssuePage && knownIssuesArticles ? (
    <KnownIssuesPage
      initKnownIssues={knownIssuesArticles}
      numberOfKnownIssues={knownIssuesCount || 0}
      categoryTree={categoriesTree}
      followedKnownIssuesIds={followedKnownIssuesIds}
    />
  ) : (
    <CategoryPageWrapper
      key={category.id}
      currentCategory={category}
      categories={categoriesTree}
      categoriesArticles={categoriesArticles}
    />
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ req, res, locale, query, helpcenterData, experiments }) => {
    const { categoryName } = query;
    const logger = createLogger(LoggerNamespaces.Server);
    const useV2 = isKbServiceEnabled(experiments);

    let category: Category;
    try {
      category = await getCategoryByUri(
        req.aspects,
        res.locals.appContext,
        `/${categoryName}`,
        locale,
        res.locals.redisClient,
        useV2
      );
    } catch (e: any) {
      const statusError = e.httpStatusCode || e.statusCode || e.code;
      const fullUrl = `https://support.wix.com/${locale}/${categoryName}`;

      if (statusError === HttpStatusCode.NOT_FOUND) {
        const wixDocsUrl = await getWixDocsUrl(res.locals.appContext, fullUrl);

        if (wixDocsUrl) {
          const userAgent = req.headers['user-agent'];
          if (isProduction() && userAgent && !isSledUserAgent(userAgent)) {
            const logData = {
              from: fullUrl,
              to: wixDocsUrl,
              referrer: req.headers.referer,
            };
            logger.info('Redirecting to Wix Docs', logData);
          }

          return {
            redirect: {
              statusCode: 301,
              destination: wixDocsUrl,
            },
          };
        }
      }
      throw e;
    }

    if (category.uri !== `/${categoryName}`) {
      return {
        redirect: {
          statusCode: HttpStatusCode.MOVED_PERMANENTLY,
          destination: encodeURI(`/${locale}/${category.uri}`),
        },
      };
    }

    const { aspects } = req;

    const { categories: categoriesTree } = helpcenterData;

    const useStaticSeo = isKbServiceEnabled(experiments);
    const pageSeo = useStaticSeo
      ? null
      : await getPageSeo(
        req.aspects,
        locale,
        PageType.Category,
        res.locals.redisClient
      ).catch(devFallback(null));
    category = findCategoryInTree(categoriesTree, category.uri) as Category;

    if (!category) {
      logger
        .withRequest(req)
        .error(
          `Category /${locale}/${categoryName} cannot be not found in tree`
        );
      return {
        notFound: true,
      };
    }

    const isKnownIssueCategory = category.id === KNOWN_ISSUE_CATEGORY_ID;

    let categoriesArticles: Record<string, Article[]> = {};
    if (!isKnownIssueCategory) {
      if (!category.children || category.children.length === 0) {
        categoriesArticles = await getCategoryArticles(
          aspects,
          res.locals.appContext,
          [category.id],
          locale,
          useV2
        );
      } else {
        const childrenWithNoChildren = category.children.filter(
          (child) => !child.children || child.children.length === 0
        );
        const childrenIds = childrenWithNoChildren.map((child) => child.id);

        if (childrenIds.length > 0) {
          categoriesArticles = await getCategoryArticles(
            aspects,
            res.locals.appContext,
            childrenIds,
            locale,
            useV2
          );
        }
      }
    }

    const translations = await getCategoryTranslations(
      aspects,
      res.locals.appContext,
      category.id,
      res.locals.redisClient,
      useV2
    );

    const knownIssuesArticles = isKnownIssueCategory
      ? await searchArticles(
        aspects,
        res.locals.appContext,
        locale,
        {
          page: 1,
          pageSize: KNOWN_ISSUES_ITEMS_PER_PAGE,
          resolutions: KNOWN_ISSUES_RELEVANT_RESOLUTIONS,
          sortType: ArticleSortType.CREATION_DATE_DESC,
          text: '',
        },
        res.locals.redisClient,
        true,
        useV2
      )
      : undefined;

    return {
      props: {
        pageType: isKnownIssueCategory
          ? PageType.KnownIssues
          : PageType.Category,
        category,
        categoriesTree,
        categoriesArticles,
        pageData: {
          category,
          translations,
        },
        itemId: category.id,
        knownIssuesArticles: knownIssuesArticles?.items || null,
        knownIssuesCount: knownIssuesArticles?.itemsCount || null,
        pageSeo,
      },
    };
  });

export default CategoryPage;
